import React, { JSX, useCallback, useEffect, useState } from 'react';
import ReactDomServer from 'react-dom/server';
import { FormProvider, useForm } from 'react-hook-form';
import { WarningIcon } from '@chakra-ui/icons';
import { Badge, Box, Button, Flex, Progress, Text, useTheme } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios/index';
import { MainPageContent, PageHeading, useToast } from 'common/components';
import { useAssignProductsToRestaurant } from 'services/product-assignment';

import ItemsSummaryModal from '../../common/components/ItemsSummaryModal/ItemsSummaryModal';
import BackgroundScanner from '../../common/components/Scanner/BackgroundScanner';
import BluetoothScanner from '../../common/components/Scanner/BluetoothScanner';
import HIDScanner from '../../common/components/Scanner/HIDScanner';
import ScanditScanner from '../../common/components/Scanner/ScanditScanner';
import ScannerSettingsQRCode, { SettingsStep } from '../../common/components/Scanner/ScannerSettingsQRCode';
import { useBlockNavigation } from '../../common/hooks/useBlockNavigation';
import { HttpException } from '../../common/models/httpException';
import { useExecuteWithConfirmation } from '../../common/utils';
import { extractUId } from '../../common/utils/qrCodes.helper';
import { useTranslations } from '../../contexts/LocalizationContext';
import { useHtmlTranslations } from '../../contexts/LocalizationContext/hooks';
import { useProductByUid } from '../../services/products';
import { ProductResponse } from '../Products/Product.model';
import { AssignProductFromCsvModal } from './AssignProductFromCsv/AssignProductFromCsvModal';
import { AssignItemsToRestaurantResponse } from './AssignProductFromCsv/model/AssignItemsToRestaurant.model';
import { ReassignBetweenRestaurantsModal } from './ReassignBetweenRestaurants/ReassignBetweenRestaurantsModal';
import { getItemAssignmentValidationSchema } from './AssignProduct.form';
import { ProductAssignmentForm, ScannerType, VisibleModal } from './AssignProduct.model';
import AssignProductForm from './AssignProdutForm';
import ProductAssignmentTable from './ProductAssignmentTable';
import ScanInfoModal from './ScanInfoModal';

const LIST_WRAPPER_HEIGHT = '400px';

const AssignProduct = (): JSX.Element => {
  const translations = useTranslations();
  const htmlTranslations = useHtmlTranslations();
  const { displayToast } = useToast();
  const { colors } = useTheme();
  const [scannedProducts, setScannedProducts] = useState<ProductResponse[]>([]);
  const [code, setCode] = useState<string | null>(null);
  const [visibleModal, setVisibleModal] = useState<VisibleModal | null>(null);
  const [isMassAssign, setIsMassAssign] = useState(false);
  const [assignedItems, setAssignedItems] = useState<AssignItemsToRestaurantResponse>();
  const [scannerType, setScannerType] = useState<ScannerType>(ScannerType.SCANDIT);
  const [settingsSteps, setSettingsSteps] = useState<SettingsStep[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [scannedProduct, setScannedProduct] = useState<ProductResponse | null>(null);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  // const { isOpen: isLeaveConfirmOpen, onOpen: onLeaveConfirmOpen, onClose: onLeaveConfirmClose } = useDisclosure();

  const handleShowAssignSummary = (data: AssignItemsToRestaurantResponse) => {
    setAssignedItems(data);
    setVisibleModal(VisibleModal.PRODUCTS_ASSIGNMENT_SUCCESS);
  };

  const { mutate: assignProductsToRestaurant, isLoading: isAssignProductsInProgress } = useAssignProductsToRestaurant({
    onSuccessfulAssign: (data) => handleShowAssignSummary(data),
  });

  const formReturn = useForm<ProductAssignmentForm>({
    resolver: yupResolver(getItemAssignmentValidationSchema(translations)),
    defaultValues: { itemsVolume: null, restaurant: null },
  });

  const { trigger, getValues, watch, reset } = formReturn;

  const watchItemsVolume = watch('itemsVolume');
  const watchRestaurant = watch('restaurant');

  const isAlreadyScanned = useCallback(
    (uId: string | null): boolean => {
      return scannedProducts.some((item) => item.uId === uId);
    },
    [scannedProducts],
  );

  const { mutate: getRelevo, isLoading: isLoadingGetRelevo } = useProductByUid();

  const handleScan = async (qrCode: string) => {
    await trigger();
    if (!watchItemsVolume || !watchRestaurant) {
      setCode(null);
      displayToast('error', translations('assign_products_empty_form_error'), false, 2000);
      return;
    }
    if (!!watchItemsVolume && scannedProducts.length === Number(watchItemsVolume)) {
      setCode(null);
      displayToast('error', translations('product_assignment_too_many_scanned_products_length_error'), false, 4000);
      return;
    }
    getRelevo(
      { uId: qrCode },
      {
        onSuccess: (productByUidResult) => {
          if (productByUidResult) {
            setCode(null);
            setScannedProducts((products) => [productByUidResult, ...products]);
            setScannedProduct(productByUidResult);
            setIsDuplicate(false);
            setVisibleModal(VisibleModal.SCAN_INFO);
          }
        },
        onError: (err: AxiosError<HttpException>) => {
          setCode(null);
          const message = err.response?.data.message;
          displayToast('error', message ?? translations('product_assignment_product_fetching_error'), false, 4000);
        },
      },
    );
  };

  useEffect(() => {
    if (code) {
      const uId = extractUId(code);
      if (uId && isAlreadyScanned(uId)) {
        // Handle duplicate
        const duplicate = scannedProducts.find((item) => item.uId === uId);
        if (duplicate) {
          setScannedProduct(duplicate);
          setIsDuplicate(true);
          setVisibleModal(VisibleModal.SCAN_INFO);
        }
        return;
      }
      if (uId) {
        void handleScan(uId);
      }
    }
    // FYI: handleScan - consider using useEffectEvent once it is released in a stable version of React, and we upgrade it
    // https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event
    // eslint-disable-next-line
  }, [code, isAlreadyScanned, scannedProducts]);

  const triggerAssignProducts = async () => {
    await trigger();
    if (!getValues('itemsVolume')) {
      displayToast('error', translations('product_assignment_empty_items_number_input_error'));
      return;
    }

    if (!!getValues('itemsVolume') && !(Number(getValues('itemsVolume')) === scannedProducts.length)) {
      displayToast('error', translations('product_assignment_incorrect_scanned_products_length_error'));
      return;
    }

    await assignProductsToRestaurant({
      restaurantId: getValues('restaurant')!.id,
      productIds: scannedProducts.map(({ id }) => id),
    });
  };

  const handleProductDelete = (uId: string | null) => {
    const productsAfterDelete = scannedProducts.filter((product) => product.uId !== uId);
    setScannedProducts(productsAfterDelete);
    setVisibleModal(null);
    setScannedProduct(null);
    setIsDuplicate(false);
    setCode(null);
  };

  const handleCancelClicked = () => {
    setScannedProducts([]);
    reset({ restaurant: null, itemsVolume: null });
    setVisibleModal(null);
    setScannedProduct(null);
    setIsDuplicate(false);
    setCode(null);
  };

  const { executeWithConfirmation: handleCancel } = useExecuteWithConfirmation(
    translations('product_assignment_items_cancel_confirmation_modal_content'),
    handleCancelClicked,
  );

  const handleMassAssignClick = () => {
    setIsMassAssign(true);
    setVisibleModal(VisibleModal.ASSIGN_PRODUCTS_FROM_CSV);
  };

  const handleAssignFromCsvClick = () => {
    setIsMassAssign(false);
    setVisibleModal(VisibleModal.ASSIGN_PRODUCTS_FROM_CSV);
  };

  const handleReassignBetweenRestaurantsClick = () => setVisibleModal(VisibleModal.REASSIGN_BETWEEN_RESTAURANTS);

  const handleCloseModal = () => {
    setVisibleModal(null);
    setScannedProduct(null);
    setIsDuplicate(false);
    setCode(null);
  };

  // Set up the beforeunload event handler to show a confirmation dialog when the user tries to leave the page
  useEffect(() => {
    // Only add the event listener if there are scanned products or if the form has been filled out
    const hasUnsavedChanges = scannedProducts.length > 0 || !!watchItemsVolume || !!watchRestaurant;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Standard way to show a confirmation dialog when leaving the page
        // The message is controlled by the browser and may not be displayed in some browsers
        const message = translations('product_assignment_items_cancel_confirmation_modal_content');
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [scannedProducts.length, watchItemsVolume, watchRestaurant, translations]);

  // Use the custom hook to block in-app navigation when there are unsaved changes
  const hasUnsavedChanges = scannedProducts.length > 0; // || !!watchItemsVolume || !!watchRestaurant;
  useBlockNavigation(hasUnsavedChanges, translations('product_assignment_items_cancel_confirmation_modal_content'));

  // Reset modal state when component mounts to prevent modals from appearing after page refresh
  useEffect(() => {
    setVisibleModal(null);
    setScannedProduct(null);
    setIsDuplicate(false);
    setCode(null);
  }, []);

  const handleScannerTypeChange = (type: ScannerType) => {
    // If switching to a different scanner type, reset connection status
    if (type !== scannerType) {
      setIsConnected(false);
    }

    // Update scanner type
    setScannerType(type);
  };

  const handleScannerConnectionChange = (connected: boolean) => {
    setIsConnected(connected);
  };

  const handleShowBluetoothSettings = () => {
    // Create steps array for multi-step settings
    const steps: SettingsStep[] = [
      {
        type: 'text',
        title: 'Bluetooth Scanner Setup',
        content: 'Follow these steps to set up your Bluetooth scanner:',
      },
      {
        type: 'qr',
        title: '1. Bluetooth BLE Pairing',
        content: '%%SpecCodeAC',
      },
      {
        type: 'text',
        title: '2. Confirm Connection',
        content: 'A red BLEx icon will appear on the scanner.',
      },
      {
        type: 'qr',
        title: '3. Rename 1/2 - optional',
        content: '%%SpecCodeEC',
      },
      {
        type: 'qr',
        title: '3. Rename 2/2 - optional',
        content: 'Relevo Scanner',
      },
      {
        type: 'text',
        title: '4. Connect procedure',
        content:
          'After each scanner disconnection (due to power off or leaving the page), you need to click “Connect Bluetooth Scanner” and select your scanner from the list – Relevo Scanner (or BarCode Scanner BLE).',
      },
    ];

    setSettingsSteps(steps);
    setVisibleModal(VisibleModal.BLUETOOTH_SCANNER_SETTINGS);
  };

  const handleShowHIDSettings = () => {
    // Create steps array for multi-step settings
    const steps: SettingsStep[] = [
      {
        type: 'text',
        title: 'USB Dongle (HID) Setup',
        content: 'Follow these steps to set up your USB Dongle scanner:',
      },
      {
        type: 'text',
        title: '1. Connect USB Dongle',
        content: 'Ensure the USB dongle or the docking/charging station is properly connected to your computer.',
      },
      {
        type: 'qr',
        title: '2. Configure Scanner',
        content: '%%SpecCodeA8',
      },
      {
        type: 'text',
        title: '3. Test Connection',
        content: 'Scan any barcode to test if the connection is working properly.',
      },
      {
        type: 'text',
        title: '4. Having trouble?',
        content: 'Try scanning the code on the bottom of the charging base and plugging it back in.',
      },
    ];

    setSettingsSteps(steps);
    setVisibleModal(VisibleModal.HID_SCANNER_SETTINGS);
  };

  const handleShowBackgroundSettings = () => {
    // Create steps array for multi-step settings
    const steps: SettingsStep[] = [
      {
        type: 'text',
        title: 'USB Dongle (HID) Setup',
        content: 'Follow these steps to set up your USB Dongle scanner:',
      },
      {
        type: 'text',
        title: '1. Connect USB Dongle',
        content: 'Ensure the USB dongle or the docking/charging station is properly connected to your computer.',
      },
      {
        type: 'qr',
        title: '2. Configure Scanner',
        content: '%%SpecCodeA8',
      },
      {
        type: 'qr',
        title: '3.1 Add prefix to the barcode',
        content: '%%SpecCode9A',
      },
      {
        type: 'qr',
        title: '3.2 Add prefix to the barcode',
        content: '%%40',
      },
      {
        type: 'qr',
        title: '3.2 Add prefix to the barcode',
        content: '%%40',
      },
      {
        type: 'qr',
        title: '4.1 Add suffix to the barcode',
        content: '%%SpecCode9B',
      },
      {
        type: 'qr',
        title: '4.2 Add suffix to the barcode',
        content: '%%23',
      },
      {
        type: 'qr',
        title: '4.2 Add suffix to the barcode',
        content: '%%23',
      },
      {
        type: 'qr',
        title: '4 Confirm configuration',
        content: '%%ExitSet',
      },
      {
        type: 'text',
        title: '6. Having trouble?',
        content: 'Try scanning the code on the bottom of the charging base and plugging it back in.',
      },
    ];

    setSettingsSteps(steps);
    setVisibleModal(VisibleModal.BACKGROUND_SCANNER_SETTINGS);
  };

  const handleShowFactoryReset = () => {
    // Create steps array for multi-step settings
    const steps: SettingsStep[] = [
      {
        type: 'text',
        title: 'Factory Reset Scanner',
        content:
          'WARNING: This will reset your HW0009 scanner to factory settings. All custom configurations will be lost.',
      },
      {
        type: 'qr',
        title: '2. Scan to Reset',
        content: '%%SpecCode93',
      },
    ];

    setSettingsSteps(steps);
    setVisibleModal(VisibleModal.FACTORY_RESET_SCANNER);
  };

  const handleShowPowerTimeoutTimer = () => {
    // Create steps array for multi-step settings
    const steps: SettingsStep[] = [
      {
        type: 'text',
        title: 'Power Timeout Timer',
        content:
          'Configure the power timeout timer for your HW0009 scanner. This setting determines how long the scanner will stay on before automatically powering off to save battery.',
      },
      {
        type: 'qr',
        title: '1. Scan to Set 1 Minute',
        content: '%%SpecCode31',
      },
      {
        type: 'qr',
        title: '2. Scan to Set 30 Minutes',
        content: '%%SpecCode35',
      },
      {
        type: 'qr',
        title: '3. Never',
        content: '%%SpecCode36',
      },
      {
        type: 'text',
        title: '7. Confirmation',
        content: 'After scanning, the scanner will beep to confirm the new setting has been applied.',
      },
    ];

    setSettingsSteps(steps);
    setVisibleModal(VisibleModal.POWER_TIMEOUT_TIMER);
  };

  return (
    <MainPageContent>
      <Flex direction="column" height="100%" pb={2}>
        <Box flex={1}>
          <PageHeading>{translations('product_assignment_header')}</PageHeading>
          <Flex justifyContent="space-between" direction={{ base: 'column', md: 'row' }} mb={{ base: 6, md: 4 }}>
            {/* Left column - CSV buttons */}
            <Flex direction="column" width={'100%'} flex={1}>
              <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 2, md: 0 }}>
                <Button
                  variant="secondary"
                  m={1}
                  onClick={handleAssignFromCsvClick}
                  whiteSpace="normal"
                  w={{ base: '100%', md: 'auto' }}
                >
                  {translations('product_assignment_assign_from_csv_button')}
                </Button>
                <Button
                  variant="secondary"
                  m={1}
                  onClick={handleReassignBetweenRestaurantsClick}
                  whiteSpace="normal"
                  w={{ base: '100%', md: 'auto' }}
                >
                  {translations('product_assignment_reassign_between_restaurants')}
                </Button>
                <Button
                  variant="secondary"
                  m={1}
                  onClick={handleMassAssignClick}
                  whiteSpace="normal"
                  w={{ base: '100%', md: 'auto' }}
                >
                  {translations('product_assignment_mass_assign_from_csv_button')}
                </Button>
              </Flex>
              <Flex>
                <FormProvider {...formReturn}>
                  <AssignProductForm scannedProductsLength={scannedProducts.length} width={'100%'} />
                </FormProvider>
              </Flex>
            </Flex>

            {/* Right columns - Scanner buttons */}
            <Flex
              direction="column"
              alignItems={{ base: 'stretch', md: 'flex-end' }}
              width={{ base: '100%', md: 'auto' }}
              border="1px solid"
              borderColor="blue.300"
              borderRadius="lg"
              p={{ base: 3, md: 4 }}
              boxShadow="sm"
              mb={{ base: 4, md: 0 }}
            >
              {/* Scanner HW0009 Header */}
              <Text
                fontSize="xl"
                fontWeight="bold"
                mb="4"
                pb="2"
                textAlign={{ base: 'center', md: 'right' }}
                color="blue.600"
                borderBottom="1px solid"
                borderColor="blue.200"
                width="100%"
              >
                HW0009 Settings
              </Text>

              {/* Factory Reset Scanner button */}
              <Flex mb="3" alignItems="center" justifyContent={{ base: 'space-between', md: 'flex-end' }}>
                <Button
                  onClick={handleShowFactoryReset}
                  variant={'critical'}
                  width={{ base: '100%', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Factory Reset
                </Button>
              </Flex>

              {/* Power Timeout Timer button */}
              <Flex mb="3" alignItems="center" justifyContent={{ base: 'space-between', md: 'flex-end' }}>
                <Button
                  onClick={handleShowPowerTimeoutTimer}
                  colorScheme="blue"
                  variant="outline"
                  width={{ base: '100%', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Power Timeout
                </Button>
              </Flex>
              {/* Bluetooth scanner row */}

              <Flex
                mb="3"
                alignItems="center"
                justifyContent={{ base: 'space-between', md: 'flex-end' }}
                borderBottom="1px solid"
                borderTop="1px solid"
                borderColor="blue.200"
                pb="2"
                pt="2"
              >
                <Button
                  mr="2"
                  onClick={() => handleScannerTypeChange(ScannerType.BLUETOOTH)}
                  colorScheme={scannerType === ScannerType.BLUETOOTH ? 'blue' : 'grey'}
                  width={{ base: '100%', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Connect Bluetooth
                </Button>
                <Button
                  onClick={handleShowBluetoothSettings}
                  colorScheme="blue"
                  variant="outline"
                  width={{ base: 'auto', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Settings
                </Button>
              </Flex>

              {/* HID scanner row */}
              <Flex
                mb="3"
                alignItems="center"
                justifyContent={{ base: 'space-between', md: 'flex-end' }}
                borderBottom="1px solid"
                borderColor="blue.200"
                pb="2"
              >
                <Button
                  mr="2"
                  onClick={() => handleScannerTypeChange(ScannerType.HID)}
                  colorScheme={scannerType === ScannerType.HID ? 'blue' : 'grey'}
                  width={{ base: '100%', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Connect USB Dongle (USB Base)
                </Button>
                <Button
                  onClick={handleShowHIDSettings}
                  colorScheme="blue"
                  variant="outline"
                  width={{ base: 'auto', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Settings
                </Button>
              </Flex>

              {/* Background scanner row */}
              <Flex
                mb="3"
                alignItems="center"
                justifyContent={{ base: 'space-between', md: 'flex-end' }}
                borderBottom="1px solid"
                borderColor="blue.200"
                pb="2"
              >
                <Button
                  mr="2"
                  onClick={() => handleScannerTypeChange(ScannerType.BACKGROUND)}
                  colorScheme={scannerType === ScannerType.BACKGROUND ? 'blue' : 'grey'}
                  width={{ base: '100%', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  USB - background (experimental)
                </Button>
                <Button
                  onClick={handleShowBackgroundSettings}
                  colorScheme="blue"
                  variant="outline"
                  width={{ base: 'auto', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                >
                  Settings
                </Button>
              </Flex>

              {/* Use Camera button */}
              <Flex mb="3" alignItems="center" justifyContent={{ base: 'space-between', md: 'flex-end' }}>
                <Button
                  onClick={() => handleScannerTypeChange(ScannerType.SCANDIT)}
                  colorScheme={scannerType === ScannerType.SCANDIT ? 'blue' : 'grey'}
                  width={{ base: '100%', md: '180px' }}
                  height="auto"
                  whiteSpace="normal"
                  py={2}
                  mr="2"
                >
                  Use Camera
                </Button>
                <Box width={{ base: 'auto', md: '180px' }} />
              </Flex>

              {/* Scanner indicator */}
              <Flex alignItems="center" justifyContent={{ base: 'space-between', md: 'flex-start' }}>
                <Badge
                  colorScheme={scannerType !== ScannerType.NONE ? 'green' : 'grey'}
                  p="2"
                  borderRadius="lg"
                  width={{ base: 'auto', md: '180px' }}
                  textAlign="center"
                  mr="2"
                >
                  Scanner: {scannerType}
                </Badge>
                <Box width={{ base: 'auto', md: '180px' }} />
              </Flex>
            </Flex>
          </Flex>

          <Flex
            justifyContent={{ base: 'center', xl: 'left' }}
            alignItems={{ base: 'center', xl: 'stretch' }}
            direction={{ base: 'column', xl: 'row' }}
          >
            <Box
              width={{ base: '100%', lg: '30%' }}
              height="100%"
              border="1px solid"
              borderColor={isConnected ? 'green.300' : 'grey.100'}
              borderRadius={'lg'}
              p={4}
              mr={[0, 0, 2]}
            >
              {scannerType === ScannerType.SCANDIT && (
                <>
                  <Text mb={2} color="blue.500" fontWeight="bold">
                    Using Camera Scanner
                  </Text>
                  <ScanditScanner onScan={setCode} />
                </>
              )}
              {scannerType === ScannerType.BLUETOOTH && (
                <>
                  <Text mb={2} color="blue.500" fontWeight="bold">
                    Bluetooth Scanner
                  </Text>
                  <BluetoothScanner onScan={setCode} onConnectionChange={handleScannerConnectionChange} />
                </>
              )}
              {scannerType === ScannerType.HID && (
                <>
                  <Text mb={2} color="blue.500" fontWeight="bold">
                    HID Scanner
                  </Text>
                  <HIDScanner onScan={setCode} onConnectionChange={handleScannerConnectionChange} />
                </>
              )}
              {scannerType === ScannerType.BACKGROUND && (
                <>
                  <Text mb={2} color="blue.500" fontWeight="bold">
                    Background Scanner
                  </Text>
                  <BackgroundScanner onScan={setCode} onConnectionChange={handleScannerConnectionChange} />
                </>
              )}
              {scannerType === ScannerType.NONE && (
                <Flex height="100%" justifyContent="center" alignItems="center" direction="column">
                  <WarningIcon fontSize="30px" color={colors.grey[50]} />
                  <Text fontSize="xl" color={colors.grey[200]}>
                    No scanner selected
                  </Text>
                  <Text fontSize="md" color={colors.grey[200]} mt={2}>
                    Please select a scanner type from the options above
                  </Text>
                </Flex>
              )}
            </Box>
            <Box
              width={{ base: '100%', lg: '70%' }}
              mt={{ base: 4, xl: 0 }}
              border="1px solid"
              borderColor={colors.orange[500]}
              borderRadius="lg"
            >
              {scannedProducts.length ? (
                <Flex alignItems="center" justifyContent="flex-start" direction="column" height="100%">
                  <Text fontSize="xl" mt={2}>
                    {translations('product_assignment_scanned_products_list_title')}
                  </Text>
                  {!!watchItemsVolume && (
                    <>
                      <Text color={colors.grey[900]} width="70%">
                        {translations('product_assignment_scanned_products_volume_preview', {
                          '{{actual-scanned-number}}': String(scannedProducts.length),
                          '{{desired-scanned-number}}': String(watchItemsVolume),
                        })}
                      </Text>
                      <Box width="70%" mt={2} mb={3}>
                        <Progress
                          value={(scannedProducts.length / Number(watchItemsVolume)) * 100}
                          size="md"
                          colorScheme="orange"
                          borderRadius="lg"
                          hasStripe
                        />
                      </Box>
                    </>
                  )}
                  <Box width="100%" height={LIST_WRAPPER_HEIGHT} overflowY="scroll" pb={2}>
                    <ProductAssignmentTable
                      onProductDelete={handleProductDelete}
                      data={scannedProducts}
                      isLoading={isLoadingGetRelevo}
                    />
                  </Box>
                </Flex>
              ) : (
                <Flex height="100%" justifyContent="center" alignItems="center" direction="column" p={2}>
                  <WarningIcon fontSize="30px" color={colors.grey[50]} />
                  <Text fontSize="3xl" color={colors.grey[200]}>
                    {translations('product_assignment_empty_scanned_products_list')}
                  </Text>
                </Flex>
              )}
            </Box>
          </Flex>
        </Box>
        <Flex mt={4} direction={{ base: 'column', md: 'row' }} gap={{ base: 2, md: 0 }}>
          <Button
            variant={'primary'}
            onClick={triggerAssignProducts}
            isDisabled={!scannedProducts.length}
            isLoading={isAssignProductsInProgress}
            height="auto"
            whiteSpace="normal"
            py={2}
            w={{ base: '100%', md: 'auto' }}
          >
            {translations('product_assignment_assign_items_button')}
          </Button>
          <Button
            variant="secondary"
            p={5}
            ml={{ md: 2 }}
            onClick={handleCancel}
            height="auto"
            whiteSpace="normal"
            py={2}
            w={{ base: '100%', md: 'auto' }}
          >
            {translations('cancel')}
          </Button>
        </Flex>
        <ItemsSummaryModal
          isVisible={visibleModal === VisibleModal.PRODUCTS_ASSIGNMENT_SUCCESS}
          modalHeader={translations('assign_items_summary_modal_header')}
          successText={htmlTranslations('product_assignment_items_modal_success_content', {
            '{{success-items-number}}': ReactDomServer.renderToString(
              <Text as="b">{(assignedItems?.assignedItems.length || 0).toString()}</Text>,
            ),
            '{{items-number}}': ReactDomServer.renderToString(
              <Text as="b">
                {(
                  (assignedItems?.assignedItems.length || 0) + (assignedItems?.notAssignedItems.length || 0)
                ).toString()}
              </Text>,
            ),
          })}
          failureText={translations('not_assigned_items')}
          failureItems={assignedItems?.notAssignedItems}
          successItems={assignedItems?.assignedItems.map((uId) => {
            return { uId };
          })}
          onCancel={handleCancelClicked}
        />
        <AssignProductFromCsvModal
          isVisible={visibleModal === VisibleModal.ASSIGN_PRODUCTS_FROM_CSV}
          onModalClose={handleCloseModal}
          isMassAssign={isMassAssign}
          onAssign={handleShowAssignSummary}
        />
        <ReassignBetweenRestaurantsModal
          isVisible={visibleModal === VisibleModal.REASSIGN_BETWEEN_RESTAURANTS}
          onModalClose={handleCloseModal}
          onAssign={handleShowAssignSummary}
        />

        <ScannerSettingsQRCode
          isOpen={visibleModal === VisibleModal.BLUETOOTH_SCANNER_SETTINGS}
          onClose={handleCloseModal}
          title="Bluetooth Scanner Settings"
          steps={settingsSteps}
        />

        <ScannerSettingsQRCode
          isOpen={visibleModal === VisibleModal.HID_SCANNER_SETTINGS}
          onClose={handleCloseModal}
          title="HID Scanner Settings"
          steps={settingsSteps}
        />

        <ScannerSettingsQRCode
          isOpen={visibleModal === VisibleModal.SERIAL_SCANNER_SETTINGS}
          onClose={handleCloseModal}
          title="Serial Scanner Settings"
          steps={settingsSteps}
        />

        <ScannerSettingsQRCode
          isOpen={visibleModal === VisibleModal.BACKGROUND_SCANNER_SETTINGS}
          onClose={handleCloseModal}
          title="Background Scanner Settings"
          steps={settingsSteps}
        />

        <ScannerSettingsQRCode
          isOpen={visibleModal === VisibleModal.FACTORY_RESET_SCANNER}
          onClose={handleCloseModal}
          title="Factory Reset Scanner"
          steps={settingsSteps}
        />

        <ScannerSettingsQRCode
          isOpen={visibleModal === VisibleModal.POWER_TIMEOUT_TIMER}
          onClose={handleCloseModal}
          title="Power Timeout"
          steps={settingsSteps}
        />

        <ScanInfoModal
          isVisible={visibleModal === VisibleModal.SCAN_INFO}
          onClose={handleCloseModal}
          product={scannedProduct}
          isDuplicate={isDuplicate}
        />
      </Flex>
    </MainPageContent>
  );
};

export default AssignProduct;
